"use client";
import {
  Navbar,
  NavBody,
  NavItems,
  MobileNav,
  MobileNavHeader,
  MobileNavToggle,
  MobileNavMenu,
  NavbarButton,
} from "./ui/resizable-navbar";
import { useState } from "react";
import { useTranslation } from 'react-i18next';
import logooasis from "../asset/logooasis.png";
import { IconWorld, IconCheck } from "@tabler/icons-react";
import { motion, AnimatePresence } from "motion/react";

const STORE_URLS = {
  ios: 'https://apps.apple.com/us/app/oasis-oakland/id6755713254',
  android: 'https://play.google.com/store/apps/details?id=com.oasis.oakland',
};

const detectPlatformAndRedirect = () => {
  const userAgent = navigator.userAgent.toLowerCase();
  
  // Android detection - check first because some Android browsers may include "like Mac"
  if (/android/.test(userAgent)) {
    window.open(STORE_URLS.android, '_blank', 'noopener,noreferrer');
    return;
  }
  
  // Apple devices detection (iPhone, iPad, iPod, Mac)
  if (/iphone|ipad|ipod|macintosh|mac os x/.test(userAgent)) {
    window.open(STORE_URLS.ios, '_blank', 'noopener,noreferrer');
    return;
  }
  
  // Windows, Linux, other - default to Google Play
  window.open(STORE_URLS.android, '_blank', 'noopener,noreferrer');
};

export default function OasisNavbar() {
  const { t, i18n } = useTranslation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isLangModalOpen, setIsLangModalOpen] = useState(false);

  const navItems = [
    {
      name: t('nav.home'),
      link: "#home",
    },
    {
      name: t('nav.services'),
      link: "#services",
    },
    {
      name: t('nav.features'),
      link: "#features",
    },
    {
      name: t('nav.privacy'),
      link: "#privacy",
    },
  ];

  const changeLanguage = (lng: string) => {
    i18n.changeLanguage(lng);
    setIsMobileMenuOpen(false);
    setIsLangModalOpen(false);
  };

  const languages = [
    { code: 'en', label: 'English', flag: '🇺🇸' },
    { code: 'es', label: 'Español', flag: '🇲🇽' },
    { code: 'zh', label: '中文', flag: '🇨🇳' },
  ];

  const OasisLogo = () => (
    <a
      href="#home"
      className="relative z-20 mr-4 flex items-center gap-3 px-2 py-1"
    >
      <img
        src={logooasis}
        alt="Oasis Oakland logo"
        className="h-8 w-8 sm:h-10 sm:w-10 rounded-lg shadow-sm object-contain bg-white p-0.5"
      />
      <div className="flex flex-col items-start">
        <span className="text-sm sm:text-base font-bold text-primary-green dark:text-white leading-none">
          Oasis Oakland
        </span>
      </div>
    </a>
  );

  return (
    <div className="relative w-full">
      <Navbar className="fixed top-4">
        {/* Desktop Navigation */}
        <NavBody className="max-w-5xl">
          <OasisLogo />
          
          <NavItems 
            items={navItems} 
            className="text-gray-700 font-medium"
          />
          
          <div className="flex items-center gap-3">
             {/* Language Selector - Icon with Modal */}
            <div className="relative hidden lg:block">
              <button
                onClick={() => setIsLangModalOpen(!isLangModalOpen)}
                className="p-2 rounded-full hover:bg-gray-100 transition-colors text-gray-600 hover:text-primary-green"
                aria-label="Change Language"
              >
                <IconWorld className="w-6 h-6" stroke={1.5} />
              </button>

              <AnimatePresence>
                {isLangModalOpen && (
                  <>
                    <div 
                        className="fixed inset-0 z-40" 
                        onClick={() => setIsLangModalOpen(false)}
                    />
                    <motion.div
                      initial={{ opacity: 0, y: 10, scale: 0.95 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: 10, scale: 0.95 }}
                      transition={{ duration: 0.2 }}
                      className="absolute right-0 top-full mt-2 w-48 bg-white rounded-xl shadow-xl border border-gray-100 p-2 z-50 overflow-hidden"
                    >
                      <div className="flex flex-col gap-1">
                        {languages.map((lang) => (
                          <button
                            key={lang.code}
                            onClick={() => changeLanguage(lang.code)}
                            className={`flex items-center justify-between px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                              i18n.language === lang.code
                                ? "bg-primary-green/10 text-primary-green"
                                : "text-gray-600 hover:bg-gray-50"
                            }`}
                          >
                            <span className="flex items-center gap-2">
                              <span>{lang.flag}</span>
                              <span>{lang.label}</span>
                            </span>
                            {i18n.language === lang.code && (
                              <IconCheck className="w-4 h-4" stroke={2} />
                            )}
                          </button>
                        ))}
                      </div>
                    </motion.div>
                  </>
                )}
              </AnimatePresence>
            </div>

            <NavbarButton 
                variant="primary" 
                onClick={detectPlatformAndRedirect}
                className="bg-primary-green text-white hover:bg-primary-green-dark border-none shadow-md cursor-pointer"
            >
              {t('hero.googlePlayLabel') || "Download Now"}
            </NavbarButton>
          </div>
        </NavBody>

        {/* Mobile Navigation */}
        <MobileNav>
          <MobileNavHeader>
            <OasisLogo />
            <MobileNavToggle
              isOpen={isMobileMenuOpen}
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            />
          </MobileNavHeader>

          <MobileNavMenu
            isOpen={isMobileMenuOpen}
            onClose={() => setIsMobileMenuOpen(false)}
          >
            {navItems.map((item, idx) => (
              <a
                key={`mobile-link-${idx}`}
                href={item.link}
                onClick={() => setIsMobileMenuOpen(false)}
                className="relative text-lg font-medium text-gray-800 dark:text-neutral-300 py-2"
              >
                <span className="block">{item.name}</span>
              </a>
            ))}
            
            <div className="w-full h-px bg-gray-100 my-2"></div>
            
            <div className="mb-4">
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3 px-1">Language</p>
                <div className="grid grid-cols-1 gap-2">
                    {languages.map((lang) => (
                    <button
                        key={lang.code}
                        onClick={() => changeLanguage(lang.code)}
                        className={`flex items-center justify-between px-4 py-3 rounded-xl border transition-all ${
                        i18n.language === lang.code
                            ? "bg-primary-green/5 border-primary-green text-primary-green"
                            : "border-gray-100 text-gray-600 hover:bg-gray-50"
                        }`}
                    >
                        <span className="flex items-center gap-3">
                            <span className="text-xl">{lang.flag}</span>
                            <span className="font-medium">{lang.label}</span>
                        </span>
                        {i18n.language === lang.code && (
                            <div className="w-5 h-5 rounded-full bg-primary-green flex items-center justify-center">
                                <IconCheck className="w-3 h-3 text-white" stroke={3} />
                            </div>
                        )}
                    </button>
                    ))}
                </div>
            </div>

            <div className="flex w-full flex-col gap-4">
              <NavbarButton
                onClick={() => {
                  setIsMobileMenuOpen(false);
                  detectPlatformAndRedirect();
                }}
                variant="primary"
                className="w-full bg-primary-green text-white hover:bg-primary-green-dark justify-center cursor-pointer"
              >
                {t('hero.googlePlayLabel') || "Download Now"}
              </NavbarButton>
            </div>
          </MobileNavMenu>
        </MobileNav>
      </Navbar>
    </div>
  );
}
